import type { App, Project } from '@hangar/shared';
import { toast } from 'sonner';
import { useProfileMutations } from '@/api/hooks';
import type { Profile } from '@/api/schemas';
import { showMutationError } from '@/lib/mutationError';
import { ConfirmArchiveDialog } from '@/components/ConfirmArchiveDialog';
import { archiveEnvironments, environmentsSuffix, platformLabel } from './profileListModel';

interface Props {
  profile: Profile | null;
  app: App;
  project: Project;
  onOpenChange: (open: boolean) => void;
}

/** What stops working once the profile is archived. */
function archiveImpact(profile: Profile | null, project: Project) {
  const platform = platformLabel(profile);
  return [
    {
      text: (
        <>
          <code className="font-mono text-xs text-foreground">hangar build --profile {profile?.slug}</code> stops working for <strong>{platform}</strong>
          {environmentsSuffix(archiveEnvironments(profile, project))}.
        </>
      ),
    },
    { text: 'Builds already started finish; their history, artifacts and env snapshots stay.' },
    { text: 'A new profile can use the same slug.' },
  ];
}

/** Archive a build profile (`PATCH /profiles/{id}` with `archived: true`) after listing what stops working. */
export function ArchiveProfileDialog({ profile, app, project, onOpenChange }: Props) {
  const { update } = useProfileMutations(app.id);

  const onConfirm = async () => {
    if (!profile) return;
    try {
      await update.mutateAsync({ profileId: profile.id, body: { archived: true }, etag: profile.etag });
      toast.success(`${profile.name} archived`, { description: `hangar build --profile ${profile.slug} no longer runs for ${platformLabel(profile)}.` });
      onOpenChange(false);
    } catch (e) {
      showMutationError(e, 'Could not archive the profile');
    }
  };

  return (
    <ConfirmArchiveDialog
      open={Boolean(profile)}
      onOpenChange={onOpenChange}
      testId="profile-archive-dialog"
      title={`Archive ${profile?.name ?? 'the profile'}?`}
      description={`The profile leaves ${app.name}; nothing is deleted.`}
      impact={archiveImpact(profile, project)}
      confirmLabel="Archive profile"
      busy={update.isPending}
      onConfirm={() => void onConfirm()}
    />
  );
}
